import {
  ActionIcon,
  Button,
  Card,
  Collapse,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconPlayerPlay,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { EvaluationAxis, RunRequest } from "../api/types";

const EXAMPLES: Record<string, string> = {
  "— выбери пример —": "",
  "Монолит → микросервисы":
    "Монолитное веб-приложение работает медленно при росте нагрузки, но переход на микросервисы увеличит операционную сложность — потребуется service mesh, distributed tracing, новые практики деплоя и мониторинга, чего сейчас в команде нет.",
  "Команда растёт 5→20":
    "Разработка движется хорошо, пока команда была 5 человек: общий контекст, быстрые решения в чате, каждый знает всё. После роста до 20: coordination overhead взрывается — meetings по 4 часа в день, решения принимаются неделями.",
  "Code review bottleneck":
    "В команде 10 разработчиков, из них 3 senior и 7 middle/junior. Все pull request'ы требуют апрува senior'а, и senior'ы почти всегда перегружены — средний lead time на review вырос до 2 рабочих дней.",
};

const ANALYST_MODELS = ["claude-haiku-4-5-20251001", "claude-sonnet-4-6"];
const WORKER_MODELS = [
  "claude-sonnet-4-6",
  "deepseek-reasoner",
  "deepseek-chat",
  "claude-haiku-4-5-20251001",
];
const CRITIC_MODELS = [
  "deepseek-reasoner",
  "claude-opus-4-7",
  "deepseek-chat",
  "claude-sonnet-4-6",
];

type BranchMode = "refine" | "rewrite";

interface TaskFormValues {
  task: string;
  example: string;
  analyst: string;
  worker: string;
  critic: string;
  useAxes: boolean;
  axes: EvaluationAxis[];
  branchMode: BranchMode;
}

interface TaskFormProps {
  parentRunId?: string;
  initialTask?: string;
}

export function TaskForm({ parentRunId, initialTask = "" }: TaskFormProps) {
  const navigate = useNavigate();
  const [modelsOpen, { toggle: toggleModels }] = useDisclosure(false);
  const [axesOpen, { toggle: toggleAxes }] = useDisclosure(false);

  // Парент существует → ветвление: textarea пустой, ждём только уточнение.
  // На сабмит задача склеится: parent_task + [Уточнения] + new_text.
  // В rewrite-режиме textarea предзаполняется парентом и шлётся as-is.
  const isBranching = Boolean(parentRunId);

  const form = useForm<TaskFormValues>({
    initialValues: {
      task: isBranching ? "" : initialTask,
      example: "— выбери пример —",
      analyst: ANALYST_MODELS[0],
      worker: WORKER_MODELS[0],
      critic: CRITIC_MODELS[0],
      useAxes: false,
      axes: [],
      branchMode: "refine",
    },
    validate: {
      task: (value) =>
        value.trim().length < 20 ? "Опиши задачу подробнее (минимум 20 символов)" : null,
      axes: (value, values) =>
        values.useAxes && value.length === 0
          ? "Добавь хотя бы одну ось или выключи advanced mode"
          : null,
    },
  });

  const runMutation = useMutation({
    mutationFn: (req: RunRequest) => api.createRun(req),
    onSuccess: (resp) => {
      navigate(`/runs/${resp.run_id}`);
    },
  });

  const onSubmit = form.onSubmit((values) => {
    let finalTask = values.task.trim();
    if (isBranching && values.branchMode === "refine") {
      // Конкатенация: парент + блок уточнений. Pipeline решит на этом
      // расширенном контексте — родитель остаётся в видимости agent'ов.
      finalTask = `${initialTask}\n\n[Уточнения и новые вводные]\n${finalTask}`;
    }
    runMutation.mutate({
      task: finalTask,
      models: {
        analyst: values.analyst,
        worker: values.worker,
        critic: values.critic,
      },
      evaluation_axes: values.useAxes ? values.axes : [],
      parent_run_id: parentRunId ?? null,
    });
  });

  const addAxis = () => {
    form.insertListItem("axes", { name: "", weight: 3, description: "" });
  };

  const removeAxis = (idx: number) => {
    form.removeListItem("axes", idx);
  };

  // Когда юзер переключает режим в branch-mode, сбрасываем textarea
  // на разумный default: refine → пустой, rewrite → предзаполнен парентом.
  const handleBranchModeChange = (mode: BranchMode) => {
    form.setFieldValue("branchMode", mode);
    if (isBranching) {
      form.setFieldValue("task", mode === "rewrite" ? initialTask : "");
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="md">
        {!isBranching && (
          <Select
            label="Готовые примеры"
            data={Object.keys(EXAMPLES)}
            value={form.values.example}
            onChange={(value) => {
              const next = value ?? "— выбери пример —";
              form.setFieldValue("example", next);
              form.setFieldValue("task", EXAMPLES[next] ?? "");
            }}
            comboboxProps={{ width: 480 }}
          />
        )}

        {isBranching && (
          <>
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Что хочешь сделать с веткой?
              </Text>
              <SegmentedControl
                value={form.values.branchMode}
                onChange={(v) => handleBranchModeChange(v as BranchMode)}
                data={[
                  { label: "Уточнить (дописать)", value: "refine" },
                  { label: "Переформулировать", value: "rewrite" },
                ]}
              />
              <Text size="xs" c="dimmed">
                {form.values.branchMode === "refine"
                  ? "Pipeline увидит исходную задачу + твои уточнения. Родитель в контексте."
                  : "Pipeline стартует с полностью новой формулировкой. Родитель остаётся только в дереве."}
              </Text>
            </Stack>

            {form.values.branchMode === "refine" && (
              <Card withBorder padding="sm" radius="sm" bg="gray.0">
                <Text size="xs" fw={600} c="dimmed" mb={4}>
                  Исходная задача (родитель)
                </Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {initialTask}
                </Text>
              </Card>
            )}
          </>
        )}

        <Textarea
          label={
            isBranching
              ? form.values.branchMode === "refine"
                ? "Уточнения / новые вводные"
                : "Новая формулировка задачи"
              : "Опиши задачу на русском"
          }
          description={
            isBranching
              ? form.values.branchMode === "refine"
                ? "Допиши только то, что добавляешь к родителю. Можно ввести уточнения, новые ограничения, поменять акценты."
                : "Полностью перепиши задачу. Pipeline не увидит родителя кроме как в дереве."
              : "Свободная формулировка проблемы или дилеммы. Лучше — с явным trade-off'ом."
          }
          placeholder={
            isBranching && form.values.branchMode === "refine"
              ? "Например: «Уточняю — у команды нет бюджета на найм; всё нужно решать существующими силами»."
              : 'Например: «Команда растёт быстро, но это ломает скорость принятия решений; разделить на squads — потеряем общий контекст».'
          }
          minRows={6}
          autosize
          maxRows={12}
          {...form.getInputProps("task")}
        />

        <Group gap={4}>
          <ActionIcon variant="subtle" onClick={toggleModels}>
            {modelsOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
          <Text size="sm" fw={500} style={{ cursor: "pointer" }} onClick={toggleModels}>
            ⚙️ Модели (advanced)
          </Text>
        </Group>

        <Collapse in={modelsOpen}>
          <Stack gap="xs">
            <Select
              label="analyst"
              description="Структурный парсинг — выделение компонент. Лёгкая модель ОК."
              data={ANALYST_MODELS}
              {...form.getInputProps("analyst")}
            />
            <Select
              label="worker"
              description="Формулировка противоречий + генерация решений. Sonnet проверен default."
              data={WORKER_MODELS}
              {...form.getInputProps("worker")}
            />
            <Select
              label="critic"
              description="Оценка решений. R1 = Opus по качеству на baseline N=54, в 22× дешевле."
              data={CRITIC_MODELS}
              {...form.getInputProps("critic")}
            />
          </Stack>
        </Collapse>

        <Group gap={4}>
          <ActionIcon variant="subtle" onClick={toggleAxes}>
            {axesOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
          <Text size="sm" fw={500} style={{ cursor: "pointer" }} onClick={toggleAxes}>
            🎯 Свои критерии оценки (advanced)
          </Text>
        </Group>

        <Collapse in={axesOpen}>
          <Stack gap="xs">
            <Switch
              label="Использовать пользовательские оси"
              description="Если включено — критик оценит каждое решение по этим осям. Concluder выберет top по weighted_score (после ТРИЗ-фильтра качества)."
              {...form.getInputProps("useAxes", { type: "checkbox" })}
            />

            {form.values.useAxes && (
              <Stack gap="xs">
                {form.values.axes.map((_axis, idx) => (
                  <Group key={idx} gap="xs" align="flex-start" wrap="nowrap">
                    <TextInput
                      placeholder="Название (например, «скорость внедрения»)"
                      flex={2}
                      {...form.getInputProps(`axes.${idx}.name`)}
                    />
                    <NumberInput
                      placeholder="вес"
                      min={0.5}
                      max={10}
                      step={0.5}
                      w={100}
                      {...form.getInputProps(`axes.${idx}.weight`)}
                    />
                    <TextInput
                      placeholder="описание (опционально)"
                      flex={3}
                      {...form.getInputProps(`axes.${idx}.description`)}
                    />
                    <Tooltip label="Удалить ось">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => removeAxis(idx)}
                        aria-label="Удалить"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                ))}

                <Button
                  variant="default"
                  leftSection={<IconPlus size={16} />}
                  onClick={addAxis}
                  size="xs"
                >
                  Добавить ось
                </Button>

                {form.errors.axes && (
                  <Text size="xs" c="red">
                    {form.errors.axes}
                  </Text>
                )}
              </Stack>
            )}
          </Stack>
        </Collapse>

        <Button
          type="submit"
          size="md"
          leftSection={<IconPlayerPlay size={18} />}
          loading={runMutation.isPending}
          disabled={!form.values.task.trim() || runMutation.isPending}
        >
          {runMutation.isPending
            ? "Запускаю…"
            : isBranching
            ? "Создать ветку"
            : "Запустить ТРИЗ-анализ"}
        </Button>

        {runMutation.isError && (
          <Text c="red" size="sm">
            Pipeline упал: {String(runMutation.error)}
          </Text>
        )}
      </Stack>
    </form>
  );
}
